import React from "react";
import { AlertCircle, CheckCircle, AlertTriangle, Info, X } from "lucide-react";
import { Toast as ToastType, useToast } from "../../context/ToastContext";

const TOAST_STYLES: Record<
  ToastType["type"],
  { bg: string; border: string; icon: string; IconComponent: React.ElementType }
> = {
  error: {
    bg: "bg-rose-50",
    border: "border-rose-200",
    icon: "text-rose-500",
    IconComponent: AlertCircle,
  },
  success: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    icon: "text-emerald-500",
    IconComponent: CheckCircle,
  },
  warning: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    icon: "text-amber-500",
    IconComponent: AlertTriangle,
  },
  info: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    icon: "text-blue-500",
    IconComponent: Info,
  },
};

const ToastItem: React.FC<{ toast: ToastType }> = ({ toast }) => {
  const { removeToast } = useToast();
  const style = TOAST_STYLES[toast.type];
  const { IconComponent } = style;

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg max-w-sm w-full ${style.bg} ${style.border} animate-in slide-in-from-top-2 duration-300`}
    >
      <IconComponent size={16} className={`mt-0.5 shrink-0 ${style.icon}`} />
      <p className="text-slate-700 text-xs font-semibold flex-1 leading-relaxed">
        {toast.message}
      </p>
      <button
        onClick={() => removeToast(toast.id)}
        className="shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
      >
        <X size={14} />
      </button>
    </div>
  );
};

const ToastContainer: React.FC = () => {
  const { toasts } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-2 items-center px-4 w-full"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
};

export default ToastContainer;
